import type { PixelData } from "ag-psd";
import { looksLikeHtml } from "@hightouch/lib/ad-studio/html-editor/looks-like-html";
import type { Doc } from "@hightouch/lib/ad-studio/html-editor/model";
import { parseDoc } from "@hightouch/lib/ad-studio/html-editor/parse";
import { serializeDoc } from "@hightouch/lib/ad-studio/html-editor/serialize";
import { captureHtmlAsImageData } from "src/pages/ad-studio/html-ad-editor/capture-html-preview";
import {
  preloadFontsIntoDocument,
  removePreloadedFonts,
} from "src/pages/ad-studio/layers/utils/preload-fonts-into-document";
import {
  measureTextLayers,
  withMeasuredTextBoxes,
  type TextMeasurement,
} from "src/pages/ad-studio/psd-export/text-measure";
import type { PsdFontResolver } from "@hightouch/lib/ad-studio/psd-export/psd-font";
import { assertHostedFontsSupportLayeredExport } from "src/pages/ad-studio/html-ad-editor/assert-hosted-fonts-support-layered-export";

export type LayeredExportArgs = {
  /** Ad HTML markup WITHOUT the visible disclaimer overlay. */
  content: string;
  fontRules: string[];
  componentAssets: Record<string, string> | null;
  apiBase: string;
  /** Own top bitmap layer: bottom-anchored and auto-sized, which the Doc's geometry can't represent. */
  applyVisibleDisclaimer?: (markup: string) => string;
  /** Absent → the CSS family goes through verbatim. */
  resolveFont?: PsdFontResolver;
  /** Tags the file as AI-generated via the standard XMP image resource. */
  machineReadableDisclosure?: boolean;
};
export type PreparedLayeredExport = {
  doc: Doc;
  composite: PixelData;
  measurements: Map<string, TextMeasurement>;
  rasterizeDoc: (doc: Doc) => Promise<PixelData>;
  disclosure?: PixelData;
};
/**
 * Real SVG (legacy/motion ads) has no layer structure. Multiple body children are rejected because
 * `parseDoc` wraps them in a synthetic unsized root while the capture renders only the first.
 */
export function canBuildLayeredExport(content: string): boolean {
  if (!looksLikeHtml(content)) return false;
  const { body } = new DOMParser().parseFromString(content, "text/html");
  return body.children.length === 1;
}

export async function withPreparedLayeredExport<T>(
  args: LayeredExportArgs,
  write: (prepared: PreparedLayeredExport) => Promise<T>,
): Promise<T> {
  const { content, fontRules, componentAssets, apiBase } = args;
  assertHostedFontsSupportLayeredExport(fontRules);
  if (!canBuildLayeredExport(content))
    throw new Error("Layered export needs single-root HTML markup");
  const doc = parseDoc(content, { componentAssets, apiBase });
  const capture = (htmlString: string) =>
    captureHtmlAsImageData({ htmlString, fontRules, componentAssets, apiBase });
  const composite = await capture(
    args.applyVisibleDisclaimer
      ? args.applyVisibleDisclaimer(content)
      : content,
  );
  assertCanvasesAgree(doc, composite);
  // Text measurement and both writers need the faces to remain loaded between captures.
  const fonts = await preloadFontsIntoDocument(fontRules);
  try {
    const measurements = measureTextLayers(doc);
    const disclosure = args.applyVisibleDisclaimer
      ? await capture(
          args.applyVisibleDisclaimer(
            serializeDoc({
              width: doc.width,
              height: doc.height,
              background: null,
              layers: [],
            }),
          ),
        )
      : undefined;
    return await write({
      doc: withMeasuredTextBoxes(doc, measurements),
      composite,
      measurements,
      rasterizeDoc: (isolated) => capture(serializeDoc(isolated)),
      disclosure,
    });
  } finally {
    removePreloadedFonts(fonts);
  }
}
/**
 * A divergence means the plan is sized against a different canvas than the pixels — fail loudly
 * rather than write a subtly wrong file. Rounded to match what `buildPsdDocument` writes.
 */
function assertCanvasesAgree(doc: Doc, composite: PixelData): void {
  const width = Math.round(doc.width);
  const height = Math.round(doc.height);
  if (width === composite.width && height === composite.height) return;
  throw new Error(
    `Layered export: doc canvas ${width}x${height} disagrees with the capture ${composite.width}x${composite.height}`,
  );
}
